/**
 * What the coach says, and — just as importantly — when it says nothing.
 *
 * Two rules hold this file together.
 *
 * **Only claims that were proven.** Every sentence traces to a fact the engine
 * established by replaying the position: a square that completes a line, a
 * reply that punishes a move, a counted number of winning lines. A learner
 * cannot tell an invented reason from a real one, so a single guess devalues
 * every true thing said before it.
 *
 * **Point before telling.** When something urgent is on the board, the coach
 * says that it is there and lets the player look for it. The answer is one
 * button away and never more than that. A coach that reads out the column every
 * turn produces a player who can follow instructions, which is not the same as
 * a player who can see the board.
 *
 * Column numbers are one-based here and nowhere else: the engine counts from
 * zero, people count from one, and the conversion happens at the last moment.
 */
import type { Trap, Urgency } from '../../../games/analysis/coach.ts';
import type { OpeningAdvice } from '../../../games/analysis/openings.ts';
import type { Verdict } from '../../../games/analysis/review.ts';

/** Engine columns are 0-based; a person counting columns starts at one. */
export function columnName(param: number): string {
  return String(param + 1);
}

export function columnList(params: readonly number[]): string {
  const names = params.map(columnName);
  if (names.length <= 1) return names[0] ?? '';
  const last = names[names.length - 1] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${last}`;
}

/**
 * One line of advice, in two halves.
 *
 * The nudge is always safe to show. The answer is what the nudge is hiding, and
 * is revealed only when the player asks — which is the whole pedagogy of this
 * screen expressed as a type.
 */
export interface CoachLine {
  /** Ranked so the panel can style urgency without re-deriving it. */
  readonly tone: 'urgent' | 'warning' | 'idea' | 'calm';
  /** Always shown. Says what kind of thing is on the board, not where. */
  readonly nudge: string;
  /** Shown on request. Null when the nudge already said everything. */
  readonly answer: string | null;
  /** The columns this line is about. */
  readonly params: readonly number[];
  /**
   * What those columns *mean*, which decides what may be drawn on the board.
   *
   * Without this the screen ghosted a disc of your own colour into whatever
   * column the line happened to name — including the trap columns, whose entire
   * point is that you must not play there. A suggestion marker that sometimes
   * marks the losing move is worse than no marker: it is a coach that lies
   * occasionally, which makes it useless the rest of the time.
   */
  readonly intent:
    /** Play here. Safe to draw as your own disc. */
    | 'play'
    /** *They* complete a line here. Drawn in their colour, as a warning. */
    | 'threat'
    /** Named only so you avoid them. Nothing may be drawn. */
    | 'none';
}

/** The urgent tactical fact, phrased so the player still gets to find it. */
export function urgencyLine(urgency: Urgency): CoachLine {
  switch (urgency.kind) {
    case 'win_available':
      return {
        tone: 'urgent',
        nudge:
          urgency.params.length > 1
            ? 'You can win right now, and in more than one place.'
            : 'You can win right now. Find the square.',
        answer: `Drop in column ${columnList(urgency.params)} to complete the line.`,
        params: urgency.params,
        intent: 'play',
      };

    case 'must_block':
      return {
        tone: 'urgent',
        nudge: 'They complete a line next move unless you stop it. Where?',
        answer: `Column ${columnName(urgency.param)} is the square they need. Take it.`,
        params: [urgency.param],
        intent: 'play',
      };

    case 'lost_to_double':
      return {
        tone: 'urgent',
        // Said plainly rather than softened. Discovering it by losing teaches
        // that the game is arbitrary; naming it teaches what a fork is.
        nudge: 'They have two winning squares. One disc cannot cover both.',
        answer: `They win at column ${columnList(urgency.params)}. Block one and they play the other — this was decided a move or two ago.`,
        params: urgency.params,
        // Their squares, not yours. Drawn in their colour or not at all.
        intent: 'threat',
      };

    case 'fork_available':
      return {
        tone: 'idea',
        nudge: 'There is a move here that gives you two winning squares at once.',
        answer: `Column ${columnList(urgency.params)} leaves two ways to finish. They can only block one.`,
        params: urgency.params,
        intent: 'play',
      };

    case 'all_moves_lose':
      return {
        tone: 'warning',
        nudge: 'Every move loses something here. Pick the one that lasts longest.',
        answer: null,
        params: [],
        intent: 'none',
      };

    case 'avoid_traps':
      return {
        tone: 'warning',
        nudge: trapNudge(urgency.traps),
        answer: trapAnswer(urgency.traps),
        params: urgency.traps.map((trap) => trap.param),
        // These are the columns that lose. Marking one as a suggestion would be
        // the exact opposite of the advice.
        intent: 'none',
      };
  }
}

/**
 * Small counts as words.
 *
 * A nudge must never contain a digit. "3 of these columns lose" is a count, but
 * on a screen whose every other number is a column it reads for a heartbeat as
 * *column 3* — which is the one thing the nudge is deliberately withholding.
 */
const COUNT_WORDS = [
  'None',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
] as const;

function countWord(count: number): string {
  return COUNT_WORDS[count] ?? 'Several';
}

function trapNudge(traps: readonly Trap[]): string {
  const verb = traps.length === 1 ? 'loses' : 'lose';
  return `${countWord(traps.length)} of these columns ${verb} on the spot. Check before you drop.`;
}

function trapAnswer(traps: readonly Trap[]): string {
  const shown = traps
    .slice(0, 3)
    .map((trap) => `${columnName(trap.param)} loses to ${columnList(trap.refutedBy)}`);
  const rest = traps.length - shown.length;
  const tail = rest > 0 ? `, and ${String(rest)} more` : '';
  return `Column ${shown.join('; column ')}${tail}.`;
}

/**
 * The opening principle, with the count that argues for it.
 *
 * The number is the point. "Play the centre" is folklore a learner has to take
 * on trust; "the centre square sits in seven winning lines where the edges
 * manage three" is an argument they can check by looking.
 */
export function openingLine(advice: OpeningAdvice): CoachLine {
  const col = advice.params[0];
  const where = col === undefined ? '' : `Column ${columnName(col)}`;
  const counted = `${String(advice.lines)} winning lines run through that square; the weakest column left offers ${String(advice.fewest)}.`;

  switch (advice.id) {
    case 'take_centre':
      return {
        tone: 'calm',
        nudge: 'Opening move. Take the square that belongs to the most winning lines.',
        answer: solvedNote(
          `${where} — the centre. ${counted}`,
          advice.solvedGame,
          'Connect 4 has been solved by exhaustive computer search: from the centre the first player can force a win, the two columns beside it draw, and the other four lose.',
        ),
        params: advice.params,
        intent: 'play',
      };

    case 'contest_centre':
      /*
       * Says the centre is free, not who left it free.
       *
       * The engine proves which column leads and whether it is empty. It does
       * not prove whose choice made it so, and an earlier draft that said "they
       * left the centre" was simply wrong whenever the player had opened
       * elsewhere themselves.
       */
      return {
        tone: 'calm',
        nudge: 'The centre is still free, and it is the best square on the board.',
        answer: solvedNote(
          `${where}. ${counted}`,
          advice.solvedGame,
          'Opening away from the centre gives up the advantage the solved game gives the first player.',
        ),
        params: advice.params,
        intent: 'play',
      };

    case 'stack_centre':
      // Likewise neutral about who put a disc there: what is proven is that the
      // column has one, and that the square above it still leads.
      return {
        tone: 'calm',
        nudge: 'The centre column is contested. The square on top of it still beats the wings.',
        answer: `${where}. ${counted}`,
        params: advice.params,
        intent: 'play',
      };

    case 'centre_is_taken':
      return {
        tone: 'calm',
        nudge: 'The centre is spoken for. Stay next to it rather than drifting to the edges.',
        answer: `${where} is worth the most of what is left. ${counted}`,
        params: advice.params,
        intent: 'play',
      };
  }
}

/** Appends the solved-game citation, but only for the game it is about. */
function solvedNote(body: string, solved: boolean, note: string): string {
  return solved ? `${body} ${note}` : body;
}

/**
 * The verdict on the move just played.
 *
 * Deliberately short. It arrives while the player is already looking at the
 * bot's reply, so a paragraph here is a paragraph nobody reads.
 */
export function verdictHeadline(verdict: Verdict): string {
  switch (verdict) {
    case 'best':
      return 'Best move.';
    case 'only':
      return 'The only move that held.';
    case 'good':
      return 'Good move.';
    case 'inaccuracy':
      return 'Inaccuracy.';
    case 'mistake':
      return 'Mistake.';
    case 'blunder':
      return 'Blunder.';
  }
}

/** Whether a verdict is worth offering to take back. */
export function worthRetrying(verdict: Verdict): boolean {
  return verdict === 'mistake' || verdict === 'blunder';
}

/** What the panel says when the position is quiet and nothing is provable. */
export const QUIET_LINE: CoachLine = {
  tone: 'calm',
  nudge: 'Nothing forced here. Build towards a line while watching what they are building.',
  answer: null,
  params: [],
  intent: 'none',
};
